import { useState, useEffect, useCallback } from 'react'
import { AreaChart } from '@/components/charts/AreaChart'
import { formatCurrency } from '@/components/charts/chartUtils'

interface ProjectionSummary {
  monthlyIncome: number
  monthlyExpenses: number
  monthlySavings: number
  annualIncome: number
  annualExpenses: number
  annualSavings: number
  savingsRate: number
  recurringIncome: number
  recurringExpenses: number
}

interface MonthlyProjection {
  month: string
  income: number
  expenses: number
  savings: number
  cumulativeSavings: number
}

interface GoalProjection {
  id: number
  name: string
  targetAmount: number
  currentAmount: number
  remaining: number
  progress: number
  monthsToGoal: number | null
  projectedDate: string | null
  targetDate: string | null
  onTrack: boolean | null
}

interface ProjectionData {
  summary: ProjectionSummary
  monthlyProjections: MonthlyProjection[]
  goalProjections: GoalProjection[]
  assumptions: {
    inflationRate: number
    projectionMonths: number
    dataPointsUsed: number
  }
}

function formatMonthLabel(monthStr: string): string {
  const [year, month] = monthStr.split('-')
  const date = new Date(parseInt(year), parseInt(month) - 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

export function Projections() {
  const [projections, setProjections] = useState<ProjectionData | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchProjections = useCallback(async () => {
    try {
      const response = await fetch('/api/projections?months=12')
      if (response.ok) {
        const data = (await response.json()) as ProjectionData
        setProjections(data)
      }
    } catch (error) {
      console.error('Error fetching projections:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProjections()
  }, [fetchProjections])

  // Transform data for area chart
  const chartData =
    projections?.monthlyProjections.map((m) => ({
      name: formatMonthLabel(m.month),
      income: m.income,
      expenses: m.expenses
    })) || []

  const hasData = projections && projections.assumptions.dataPointsUsed > 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-forest-dark">Projections</h1>
        <p className="text-sage mt-1">
          Financial forecasts based on your spending patterns
        </p>
      </div>

      {/* Projection Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-stone/10">
          <p className="text-sm text-stone">Monthly Income</p>
          <p className="text-xl font-bold text-success tabular-nums">
            {loading
              ? '...'
              : formatCurrency(projections?.summary.monthlyIncome || 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-stone/10">
          <p className="text-sm text-stone">Monthly Expenses</p>
          <p className="text-xl font-bold text-danger tabular-nums">
            {loading
              ? '...'
              : formatCurrency(projections?.summary.monthlyExpenses || 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-stone/10">
          <p className="text-sm text-stone">Monthly Savings</p>
          <p
            className={`text-xl font-bold tabular-nums ${(projections?.summary.monthlySavings || 0) >= 0 ? 'text-success' : 'text-danger'}`}
          >
            {loading
              ? '...'
              : formatCurrency(projections?.summary.monthlySavings || 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-stone/10">
          <p className="text-sm text-stone">Savings Rate</p>
          <p className="text-xl font-bold text-forest-dark">
            {loading ? '...' : `${projections?.summary.savingsRate || 0}%`}
          </p>
        </div>
      </div>

      {/* 12-Month Forecast Chart */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
        <h2 className="text-lg font-semibold text-charcoal mb-4">
          12-Month Forecast
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-sage">Loading projections...</div>
          </div>
        ) : hasData ? (
          <AreaChart
            data={chartData}
            dataKeys={[
              { key: 'income', name: 'Projected Income', color: '#4CAF50' },
              { key: 'expenses', name: 'Projected Expenses', color: '#EF5350' }
            ]}
            height={300}
            stacked={false}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-sage">Insufficient data for projections</p>
            <p className="text-sm text-stone mt-2">
              Add transaction data to see forecasts
            </p>
          </div>
        )}
      </div>

      {/* Annual Projections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <h3 className="font-semibold text-charcoal mb-2">Monthly</h3>
          <p
            className={`text-3xl font-bold tabular-nums ${(projections?.summary.monthlySavings || 0) >= 0 ? 'text-forest-dark' : 'text-danger'}`}
          >
            {formatCurrency(projections?.summary.monthlySavings || 0)}
          </p>
          <p className="text-sm text-sage mt-1">projected savings</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <h3 className="font-semibold text-charcoal mb-2">Quarterly</h3>
          <p
            className={`text-3xl font-bold tabular-nums ${(projections?.summary.monthlySavings || 0) >= 0 ? 'text-forest-dark' : 'text-danger'}`}
          >
            {formatCurrency((projections?.summary.monthlySavings || 0) * 3)}
          </p>
          <p className="text-sm text-sage mt-1">projected savings</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <h3 className="font-semibold text-charcoal mb-2">Annual</h3>
          <p
            className={`text-3xl font-bold tabular-nums ${(projections?.summary.annualSavings || 0) >= 0 ? 'text-forest-dark' : 'text-danger'}`}
          >
            {formatCurrency(projections?.summary.annualSavings || 0)}
          </p>
          <p className="text-sm text-sage mt-1">projected savings</p>
        </div>
      </div>

      {/* Goal Timeline */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
        <h2 className="text-lg font-semibold text-charcoal mb-4">
          Goal Timeline
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse text-sage">Loading...</div>
          </div>
        ) : projections?.goalProjections &&
          projections.goalProjections.length > 0 ? (
          <div className="space-y-4">
            {projections.goalProjections.map((goal) => (
              <div key={goal.id} className="p-4 bg-cream/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-charcoal">{goal.name}</h3>
                  {goal.onTrack !== null && (
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${goal.onTrack ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}
                    >
                      {goal.onTrack ? 'On Track' : 'Behind Schedule'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-stone">
                  <span>
                    {formatCurrency(goal.currentAmount)} /{' '}
                    {formatCurrency(goal.targetAmount)}
                  </span>
                  <span>•</span>
                  <span>{goal.progress}% complete</span>
                  {goal.projectedDate && (
                    <>
                      <span>•</span>
                      <span>
                        Est. completion: {formatDateLabel(goal.projectedDate)}
                        {goal.monthsToGoal && ` (${goal.monthsToGoal} months)`}
                      </span>
                    </>
                  )}
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-2 bg-stone/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      goal.progress >= 75
                        ? 'bg-success'
                        : goal.progress >= 50
                          ? 'bg-warning'
                          : goal.progress >= 25
                            ? 'bg-warning'
                            : 'bg-danger'
                    }`}
                    style={{ width: `${goal.progress}%` }}
                  />
                </div>
                {/* Milestones */}
                <div className="mt-2 flex justify-between text-xs text-stone">
                  <span className={goal.progress >= 25 ? 'text-success' : ''}>
                    25%
                  </span>
                  <span className={goal.progress >= 50 ? 'text-success' : ''}>
                    50%
                  </span>
                  <span className={goal.progress >= 75 ? 'text-success' : ''}>
                    75%
                  </span>
                  <span className={goal.progress >= 100 ? 'text-success' : ''}>
                    100%
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-sage">No goals to project</p>
            <p className="text-sm text-stone mt-2">
              Create a goal to see when you'll reach it
            </p>
          </div>
        )}
      </div>

      {/* Assumptions Note */}
      {projections?.assumptions && (
        <div className="text-sm text-stone text-center">
          Projections based on {projections.assumptions.dataPointsUsed} months of
          data. Inflation rate:{' '}
          {(projections.assumptions.inflationRate * 100).toFixed(1)}%
        </div>
      )}
    </div>
  )
}
